import { useMutation } from '@apollo/react-hooks';
import * as _ from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { ADD_AUTHENTICATION_QUERY } from '../graphQL/queries';
import { replaceEmptyString } from '../ManageRelations/utils';
import { AuthenticateFormDataConfig, ParameterTypeConfig, PrimaryParamterConfig } from './formDataConfig';
import { DialogForm } from '../../components/DialogForm';
import Delete from '@jda/lui-common-icon-library/ui/Delete';
import PlusCircle from '@jda/lui-common-icon-library/ui/PlusCircle';
import { Box, Grid, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  dialog: {
    width: '100%'
  },
  dialogTitle: {
    padding: theme.spacing(2),
    borderBottom: '1px solid'
  },
  progress: {
    color: theme.palette.background.paper
  },
  textField: {
    width: '80%'
  },
  grid: {
    width: '100%',
    borderBottom: '1px solid #EBEBEB'
  },
  headers: {
    color: theme.palette.text.secondary
  }
}));

interface AuthenticateParticipantProps {
  participant: any;
  workflowCode?: any;
  dialogOpen: true | false;
  handleDialogClose: any;
  handleDialogSubmit: any;
  edit: true | false;
  formData: any;
  refresh: any;
  formInfo: any;
  supportUser: true | false;
}
let dataConfig = [...AuthenticateFormDataConfig];

const AddAuthenticationDialog = (props: AuthenticateParticipantProps) => {
  const classes = useStyles();
  const { edit, formData } = props;
  let { participantCode } = props.participant;
  const [formInfo, setFormInfo] = React.useState<any>({});
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (props.edit) {
      const list: any = [];
      if (formData) {
        const additionalCredsObj = formData.additionalCreds;
        _.keys(additionalCredsObj || []).forEach(keyParameter => {
          const additionalParameterObj = additionalCredsObj[keyParameter];
          _.keys(additionalParameterObj || []).forEach(key => {
            if (keyParameter !== 'primary') {
              list.push({ parameterType: keyParameter, key: key, value: additionalParameterObj[key] });
            }
          });
        });
      }
      formData.identifier = formData?.additionalCreds?.primary?.identifier;
      formData.secret = formData?.additionalCreds?.primary?.secret
        ? decodeURI(formData?.additionalCreds?.primary?.secret)
        : null;
      formData.authUrl = formData?.additionalCreds?.primary?.authUrl;
      formData.authTokenHeaderName = formData?.additionalCreds?.primary?.authTokenHeaderName;
      formData.isAdditionalIdentifier = list.length > 0 ? 'yes' : 'no';
      formData.defaultAuth = formData.defaultAuth === true ? 'yes' : 'no';
      setFormInfo({
        isAdditionalIdentifier: list.length > 0 ? 'yes' : 'no',
        parameters: [...list],
        identifier: formData?.additionalCreds?.primary?.identifier || '',
        secret: formData?.additionalCreds?.primary?.secret
          ? decodeURI(formData?.additionalCreds?.primary?.secret)
          : null,
        authUrl: formData?.additionalCreds?.primary?.authUrl || '',
        authTokenHeaderName: formData?.additionalCreds?.primary?.authTokenHeaderName || ''
      });
    } else {
      setFormInfo({
        ...props.formInfo
      });
    }
  }, [formData, props.edit, props.formInfo]);

  const handleAddParameter = () => {
    const list = formInfo.parameters;
    list.push({ parameterType: 'authHeaders', key: '', value: '' });
    setFormInfo({
      ...formInfo,
      parameters: list
    });
  };

  const handleParameterChange = (e: any, index: number, label: string) => {
    formInfo.parameters[index][label] = e.target.value;
    if (label === 'parameterType' && e.target.value === 'primary') {
      formInfo.parameters[index]['key'] = 'identifier';
    }
    setFormInfo({
      ...formInfo
    });
  };

  const handleDeleteParameter = (index: number) => {
    formInfo.parameters.splice(index, 1);
    setFormInfo({
      ...formInfo
    });
  };

  const [addAuthentication] = useMutation(ADD_AUTHENTICATION_QUERY);

  const handleAuthenticationSubmit = (formDataObj: any) => {
    let additionalCredsObj: any = {};

    let authHeaderObjValues: any = {};
    let authformParamsObjValues: any = {};
    let requestHeadersObjValues: any = {};
    let authBodyParametersObjValues: any = {};

    if (formInfo.parameters) {
      _.forEach(formInfo.parameters || [], (element: any) => {
        if (element.parameterType === 'authHeaders') {
          authHeaderObjValues[element.key] = element.value;
        } else if (element.parameterType === 'formParams') {
          authformParamsObjValues[element.key] = element.value;
        } else if (element.parameterType === 'requestHeaders') {
          requestHeadersObjValues[element.key] = element.value;
        } else if (element.parameterType === 'authBodyParameters') {
          authBodyParametersObjValues[element.key] = element.value;
        }
      });
    }
    const primaryObj: any = {
      identifier: formDataObj?.identifier,
      secret: formDataObj?.secret ? decodeURI(formDataObj?.secret) : null,
      authUrl: formDataObj?.authUrl,
      authTokenHeaderName: formDataObj?.authTokenHeaderName
    };
    if (formDataObj?.primary?.authTokenHeaderName !== 'Authorization') {
      primaryObj.authTokenHeaderName = formDataObj?.primary?.authTokenHeaderName;
    }
    additionalCredsObj.authHeaders = authHeaderObjValues;
    additionalCredsObj.formParams = authformParamsObjValues;
    additionalCredsObj.requestHeaders = requestHeadersObjValues;
    additionalCredsObj.authBodyParameters = authBodyParametersObjValues;
    additionalCredsObj.primary = primaryObj;
    props.handleDialogSubmit();
    addAuthentication({
      variables: {
        participantCode: participantCode,
        workflowCode: props.workflowCode ? props.workflowCode : 'DEFAULT',
        authMode: formDataObj.authMode,
        defaultAuth: formDataObj.defaultAuth === 'yes' ? true : false,
        authContext: formDataObj.authContext,
        additionalCreds: replaceEmptyString(additionalCredsObj),
        method: edit ? 'PUT' : 'POST'
      }
    })
      .then(response1 => {
        enqueueSnackbar('Authentication details saved successfully !!', { variant: 'success', preventDuplicate: true });
        props.refresh();
        return response1;
      })
      .catch(() => {
        enqueueSnackbar('Add Authentication action failed !!', { variant: 'error', preventDuplicate: true });
      });
  };

  const getLatestValues = (dataObj: any) => {
    if (dataObj.isAdditionalIdentifier === 'no') {
      dataObj.parameters = [];
      setFormInfo({ ...dataObj });
    } else {
      const list: any = [];
      if (formData) {
        const additionalCredsObj = formData.additionalCreds;
        _.keys(additionalCredsObj || []).forEach(keyParameter => {
          const additionalParameterObj = additionalCredsObj[keyParameter];
          _.keys(additionalParameterObj || []).forEach(key => {
            if (keyParameter !== 'primary') {
              list.push({ parameterType: keyParameter, key: key, value: additionalParameterObj[key] });
            }
          });
        });
      }
      const info = { ...formInfo };
      setFormInfo({
        ...dataObj,
        parameters: [...list]
      });
    }
  };

  return (
    <div>
      <DialogForm
        key={edit ? `edit-authentication-${participantCode}` : 'new-authentication-dialog'}
        openFn={props.dialogOpen}
        closeFn={props.handleDialogClose}
        dataConfig={edit ? dataConfig : [...AuthenticateFormDataConfig]}
        handleDialogClose={props.handleDialogClose}
        handleDialogSubmit={handleAuthenticationSubmit}
        submitlabel="Submit"
        dialogId={edit ? `edit-authentication-${participantCode}` : 'new-authentication-dialog'}
        title={edit ? `Edit Authentication ${participantCode}` : 'New Authentication'}
        getLatestValues={getLatestValues}
        data={formData}
        supportUser={props.supportUser}
      >
        {formInfo.isAdditionalIdentifier === 'yes' && !props.supportUser && (
          <Box m={1}>
            <Box className={classes.grid}>
              <Grid container alignItems="center">
                <Grid item xs={10}>
                  <Typography variant="subtitle1" style={{ padding: '8px' }}>
                    Additional Identifiers
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <PlusCircle data-id="add-Button" onClick={handleAddParameter} />
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.grid} style={{ padding: '8px' }}>
              <Grid container>
                <Grid item xs={4}>
                  <Typography className={classes.headers}>Parameter</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className={classes.headers}>Key</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className={classes.headers}>Value</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box m={2}>
              {(formInfo?.parameters || '').map((paramObj: any, index: number) => (
                <Grid container style={{ width: '100%' }} spacing={3} alignItems="center">
                  <Grid item xs={3} justify="center" alignItems="flex-end">
                    <Select
                      data-id={`parameter-paramType-${index}`}
                      id={`parameter-paramType-${index}`}
                      value={paramObj.parameterType}
                      onChange={e => handleParameterChange(e, index, 'parameterType')}
                    >
                      {ParameterTypeConfig.map((item: any) => [<MenuItem value={item.value}>{item.label}</MenuItem>])}
                    </Select>
                  </Grid>
                  <Grid item xs={4}>
                    {paramObj.parameterType === 'primary' ? (
                      <Select
                        data-id={`parameter-key-${index}`}
                        id={`parameter-key-${index}`}
                        value={paramObj.key}
                        onChange={e => handleParameterChange(e, index, 'key')}
                        // disabled={props?.supportUser}
                      >
                        {PrimaryParamterConfig.map((item: any) => [
                          <MenuItem value={item.value}>{item.label}</MenuItem>
                        ])}
                      </Select>
                    ) : (
                      <TextField
                        data-id={`parameter-key-${index}`}
                        id={`parameter-key-${index}`}
                        className={classes.textField}
                        value={paramObj.key || ''}
                        onChange={e => handleParameterChange(e, index, 'key')}
                        // disabled={props?.supportUser}
                      ></TextField>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      data-id={`parameter-value-${index}`}
                      id={`parameter-value-${index}`}
                      className={classes.textField}
                      value={paramObj.value || ''}
                      onChange={e => handleParameterChange(e, index, 'value')}
                      // disabled={props?.supportUser}
                    ></TextField>
                  </Grid>
                  <Grid item xs={1}>
                    <Delete data-id="delete-parameter" onClick={() => handleDeleteParameter(index)} />
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        )}
      </DialogForm>
    </div>
  );
};
export default AddAuthenticationDialog;
